/**
 * BrandTemplateEditor — Upload a graphic template (schemat),
 * define editable text zones visually, then edit ONLY the text.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  X,
  Plus,
  Trash2,
  Loader2,
  Type,
  Download,
  Save,
  Move,
  Eye,
  Pencil,
  Image as ImageIcon,
  Check,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────

interface TemplateTextZone {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  defaultText: string;
  fontSize: number;
  fontWeight: "normal" | "bold";
  color: string;
  textAlign: "left" | "center" | "right";
}

interface BrandTemplate {
  id: string;
  userId: string;
  clientId: string | null;
  name: string;
  imageUrl: string;
  storagePath: string;
  mimeType: string;
  width: number;
  height: number;
  textZones: TemplateTextZone[];
  createdAt: string;
  updatedAt: string;
}

type EditorMode = "list" | "upload" | "define-zones" | "edit-text";

const FONT_WEIGHT_OPTIONS: { id: "normal" | "bold"; label: string }[] = [
  { id: "normal", label: "brandTemplate.font.normal" },
  { id: "bold", label: "brandTemplate.font.bold" },
];

const TEXT_ALIGN_OPTIONS: { id: "left" | "center" | "right"; label: string }[] = [
  { id: "left", label: "brandTemplate.align.left" },
  { id: "center", label: "brandTemplate.align.center" },
  { id: "right", label: "brandTemplate.align.right" },
];

// ── Component ─────────────────────────────────────────────

interface BrandTemplateEditorProps {
  selectedClientId?: string;
}

export default function BrandTemplateEditor({ selectedClientId }: BrandTemplateEditorProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [mode, setMode] = useState<EditorMode>("list");
  const [activeTemplate, setActiveTemplate] = useState<BrandTemplate | null>(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Zone definition state
  const [zones, setZones] = useState<TemplateTextZone[]>([]);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Text editing state
  const [customTexts, setCustomTexts] = useState<Record<string, string>>({});
  const [rendering, setRendering] = useState(false);
  const [renderedUrl, setRenderedUrl] = useState<string | null>(null);

  // Fetch templates
  const clientParam = selectedClientId ? `?clientId=${selectedClientId}` : "";
  const { data: templatesData, refetch: refetchTemplates } = useQuery<BrandTemplate[]>({
    queryKey: ["brand-templates", selectedClientId],
    queryFn: () => apiRequest("GET", `/api/ad-creator/brand-templates${clientParam}`),
    staleTime: 60_000,
  });
  const templates = templatesData || [];

  // ── Upload handlers ──────────────────────────────────

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("name", uploadName.trim());
      if (selectedClientId) formData.append("clientId", selectedClientId);

      const resp = await fetch("/api/ad-creator/brand-templates", {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
        body: formData,
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => null);
        throw new Error(errData?.message || `Upload failed (${resp.status})`);
      }
      const template: BrandTemplate = await resp.json();
      setUploadFile(null);
      setUploadPreview(null);
      setUploadName("");
      refetchTemplates();
      toast({
        title: t("brandTemplate.toast.uploaded"),
        description: `"${template.name}" — ${t("brandTemplate.toast.uploadedDesc")}`,
      });

      // Go directly to zone definition
      setActiveTemplate(template);
      setZones(template.textZones || []);
      setMode("define-zones");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("brandTemplate.unknownError");
      toast({
        title: t("brandTemplate.toast.uploadError"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }, [uploadFile, uploadName, selectedClientId, refetchTemplates, toast, t]);

  // ── Zone drawing on canvas ────────────────────────────

  const getRelativePosition = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): { x: number; y: number } => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      };
    },
    []
  );

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (editingZoneId) return;
      const pos = getRelativePosition(e);
      setIsDrawing(true);
      setDrawStart(pos);
      setDrawCurrent(pos);
    },
    [editingZoneId, getRelativePosition]
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDrawing) return;
      setDrawCurrent(getRelativePosition(e));
    },
    [isDrawing, getRelativePosition]
  );

  const handleCanvasMouseUp = useCallback(() => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }
    setIsDrawing(false);

    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const width = Math.abs(drawCurrent.x - drawStart.x);
    const height = Math.abs(drawCurrent.y - drawStart.y);

    // Ignore tiny draws (accidental clicks)
    if (width < 3 || height < 2) {
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }

    const newZone: TemplateTextZone = {
      id: `zone-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: t("brandTemplate.zones.defaultLabel", { n: zones.length + 1 }),
      x,
      y,
      width,
      height,
      defaultText: "",
      fontSize: 32,
      fontWeight: "bold",
      color: "#FFFFFF",
      textAlign: "center",
    };

    setZones((prev) => [...prev, newZone]);
    setEditingZoneId(newZone.id);
    setDrawStart(null);
    setDrawCurrent(null);
  }, [isDrawing, drawStart, drawCurrent, zones.length, t]);

  const updateZone = useCallback((zoneId: string, updates: Partial<TemplateTextZone>) => {
    setZones((prev) => prev.map((z) => (z.id === zoneId ? { ...z, ...updates } : z)));
  }, []);

  const removeZone = useCallback(
    (zoneId: string) => {
      setZones((prev) => prev.filter((z) => z.id !== zoneId));
      if (editingZoneId === zoneId) setEditingZoneId(null);
    },
    [editingZoneId]
  );

  // ── Save zones ────────────────────────────────────────

  const saveZonesMutation = useMutation({
    mutationFn: async () => {
      if (!activeTemplate) throw new Error("Brak szablonu");
      return apiRequest<BrandTemplate>(
        "PUT",
        `/api/ad-creator/brand-templates/${activeTemplate.id}`,
        {
          textZones: zones,
          name: activeTemplate.name,
        }
      );
    },
    onSuccess: (data: BrandTemplate) => {
      setActiveTemplate(data);
      refetchTemplates();
      toast({
        title: t("brandTemplate.toast.saved"),
        description: t("brandTemplate.toast.savedDesc"),
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("brandTemplate.toast.saveError"),
        description: t("brandTemplate.toast.saveErrorDesc"),
        variant: "destructive",
      });
    },
  });

  // ── Render ────────────────────────────────────────────

  const handleRender = useCallback(async () => {
    if (!activeTemplate) return;
    setRendering(true);
    setRenderedUrl(null);
    try {
      const resp = await apiRequest<{ renderedUrl: string }>(
        "POST",
        `/api/ad-creator/brand-templates/${activeTemplate.id}/render`,
        {
          texts: customTexts,
        }
      );
      setRenderedUrl(resp.renderedUrl);
      toast({
        title: t("brandTemplate.toast.rendered"),
        description: t("brandTemplate.toast.renderedDesc"),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("brandTemplate.toast.renderError");
      toast({
        title: t("brandTemplate.toast.renderError"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setRendering(false);
    }
  }, [activeTemplate, customTexts, toast, t]);

  // ── Delete template ───────────────────────────────────

  const handleDelete = useCallback(
    async (templateId: string) => {
      try {
        await apiRequest("DELETE", `/api/ad-creator/brand-templates/${templateId}`);
        refetchTemplates();
        if (activeTemplate?.id === templateId) {
          setActiveTemplate(null);
          setMode("list");
        }
        toast({
          title: t("brandTemplate.toast.deleted"),
          description: t("brandTemplate.toast.deletedDesc"),
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t("brandTemplate.toast.deleteError");
        toast({
          title: t("brandTemplate.toast.deleteError"),
          description: message,
          variant: "destructive",
        });
      }
    },
    [activeTemplate, refetchTemplates, toast, t]
  );

  // ── Open template for editing ─────────────────────────

  const openForTextEditing = useCallback((template: BrandTemplate) => {
    setActiveTemplate(template);
    setZones(template.textZones || []);
    const defaults: Record<string, string> = {};
    for (const zone of template.textZones || []) {
      defaults[zone.id] = zone.defaultText;
    }
    setCustomTexts(defaults);
    setRenderedUrl(null);
    setMode("edit-text");
  }, []);

  const openForZoneDefinition = useCallback((template: BrandTemplate) => {
    setActiveTemplate(template);
    setZones(template.textZones || []);
    setEditingZoneId(null);
    setMode("define-zones");
  }, []);

  // ── Drawing rectangle preview ──────────────────────────

  const drawRect =
    drawStart && drawCurrent
      ? {
          left: `${Math.min(drawStart.x, drawCurrent.x)}%`,
          top: `${Math.min(drawStart.y, drawCurrent.y)}%`,
          width: `${Math.abs(drawCurrent.x - drawStart.x)}%`,
          height: `${Math.abs(drawCurrent.y - drawStart.y)}%`,
        }
      : null;

  // Currently editing zone
  const editingZone = zones.find((z) => z.id === editingZoneId) || null;

  // ── Render ──────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-medium text-white/80">{t("brandTemplate.header")}</span>
          {templates.length > 0 && (
            <span className="text-[9px] text-purple-400/60">({templates.length})</span>
          )}
        </div>
        <div className="flex gap-1">
          {mode !== "list" && (
            <button
              onClick={() => {
                setMode("list");
                setActiveTemplate(null);
                setRenderedUrl(null);
              }}
              className="text-[9px] text-white/40 hover:text-white/60 transition-colors"
            >
              {t("brandTemplate.backToList")}
            </button>
          )}
          {mode === "list" && (
            <button
              onClick={() => setMode("upload")}
              className="text-[9px] text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-0.5"
            >
              <Plus className="w-3 h-3" /> {t("brandTemplate.addTemplate")}
            </button>
          )}
        </div>
      </div>

      {/* ── LIST MODE ── */}
      {mode === "list" && (
        <>
          {templates.length === 0 ? (
            <div className="text-center py-6 text-white/30 text-xs">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>{t("brandTemplate.list.empty")}</p>
              <p className="text-[10px] mt-1">{t("brandTemplate.list.emptyDesc")}</p>
              <Button
                size="sm"
                onClick={() => setMode("upload")}
                className="mt-3 h-7 text-[10px] bg-purple-600 hover:bg-purple-700"
              >
                <Upload className="w-3 h-3 mr-1" /> {t("brandTemplate.list.uploadButton")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="relative rounded-lg overflow-hidden border border-white/[0.08] hover:border-purple-500/40 transition-all group cursor-pointer"
                >
                  <img
                    src={tpl.imageUrl}
                    alt={tpl.name}
                    className="w-full h-24 object-cover"
                    onClick={() => openForTextEditing(tpl)}
                  />
                  <div className="p-1.5">
                    <p className="text-[10px] text-white/80 font-medium truncate">{tpl.name}</p>
                    <p className="text-[8px] text-white/40">
                      {tpl.width}×{tpl.height} ·{" "}
                      {t("brandTemplate.list.zones", { count: (tpl.textZones || []).length })}
                    </p>
                  </div>
                  <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openForZoneDefinition(tpl);
                      }}
                      className="p-1 rounded-full bg-black/60 text-white/60 hover:text-purple-400 transition-colors"
                      title="Edytuj strefy"
                    >
                      <Move className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openForTextEditing(tpl);
                      }}
                      className="p-1 rounded-full bg-black/60 text-white/60 hover:text-cyan-400 transition-colors"
                      title="Edytuj tekst"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(tpl.id);
                      }}
                      className="p-1 rounded-full bg-black/60 text-white/60 hover:text-red-400 transition-colors"
                      title={t("brandTemplate.list.deleteTooltip")}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── UPLOAD MODE ── */}
      {mode === "upload" && (
        <div className="p-2.5 rounded-lg border border-purple-500/20 bg-purple-900/10 space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileSelect}
          />
          {uploadPreview ? (
            <div className="relative w-full rounded-lg overflow-hidden border border-white/[0.1]">
              <img
                src={uploadPreview}
                alt="preview"
                className="w-full max-h-48 object-contain bg-black/50"
              />
              <button
                onClick={() => {
                  setUploadFile(null);
                  setUploadPreview(null);
                }}
                className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center gap-2 w-full h-24 rounded-lg border-2 border-dashed border-purple-500/20 text-xs text-white/40 hover:text-white/60 hover:border-purple-500/40 transition-all"
            >
              <Upload className="w-5 h-5" />
              <span>{t("brandTemplate.upload.selectFile")}</span>
            </button>
          )}
          <Input
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            placeholder={t("brandTemplate.upload.namePlaceholder")}
            className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
            maxLength={200}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={uploading || !uploadFile || !uploadName.trim()}
              className="flex-1 h-7 text-[10px] bg-purple-600 hover:bg-purple-700 text-white"
            >
              {uploading ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Upload className="w-3 h-3 mr-1" />
              )}
              {t("brandTemplate.upload.submit")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setMode("list");
                setUploadFile(null);
                setUploadPreview(null);
                setUploadName("");
              }}
              className="h-7 text-[10px] text-white/40 hover:text-white"
            >
              {t("brandTemplate.upload.cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* ── DEFINE ZONES MODE ── */}
      {mode === "define-zones" && activeTemplate && (
        <div className="space-y-2">
          <p className="text-[10px] text-white/50">{t("brandTemplate.zones.instruction")}</p>

          {/* Canvas with template image */}
          <div
            ref={canvasRef}
            className="relative rounded-lg overflow-hidden border border-purple-500/30 cursor-crosshair select-none"
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={() => {
              if (isDrawing) {
                setIsDrawing(false);
                setDrawStart(null);
                setDrawCurrent(null);
              }
            }}
          >
            <img
              src={activeTemplate.imageUrl}
              alt={activeTemplate.name}
              className="w-full pointer-events-none"
              draggable={false}
            />

            {/* Existing zones */}
            {zones.map((zone) => (
              <div
                key={zone.id}
                className={`absolute border-2 transition-colors cursor-pointer ${
                  editingZoneId === zone.id
                    ? "border-purple-400 bg-purple-400/20"
                    : "border-cyan-400/60 bg-cyan-400/10 hover:bg-cyan-400/20"
                }`}
                style={{
                  left: `${zone.x}%`,
                  top: `${zone.y}%`,
                  width: `${zone.width}%`,
                  height: `${zone.height}%`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingZoneId(zone.id);
                }}
              >
                <span className="absolute top-0 left-0 px-1 py-0.5 text-[8px] bg-black/70 text-white rounded-br">
                  {zone.label}
                </span>
                {zone.defaultText && (
                  <div
                    className="w-full h-full flex items-center justify-center p-1 overflow-hidden"
                    style={{
                      color: zone.color,
                      fontSize: `${Math.min(14, zone.fontSize * 0.3)}px`,
                      fontWeight: zone.fontWeight,
                      textAlign: zone.textAlign,
                    }}
                  >
                    <span className="truncate">{zone.defaultText}</span>
                  </div>
                )}
              </div>
            ))}

            {/* Drawing preview */}
            {drawRect && (
              <div
                className="absolute border-2 border-dashed border-yellow-400 bg-yellow-400/10 pointer-events-none"
                style={drawRect}
              />
            )}
          </div>

          {/* Zone editor panel */}
          {editingZone && (
            <div className="p-2.5 rounded-lg border border-purple-500/20 bg-purple-900/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-purple-300 font-medium">
                  {t("brandTemplate.zones.editing", { label: editingZone.label })}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => removeZone(editingZone.id)}
                    className="p-1 rounded text-red-400/60 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setEditingZoneId(null)}
                    className="p-1 rounded text-white/40 hover:text-white transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[9px] text-white/40">
                    {t("brandTemplate.zones.labelField")}
                  </Label>
                  <Input
                    value={editingZone.label}
                    onChange={(e) => updateZone(editingZone.id, { label: e.target.value })}
                    className="h-6 bg-[#030407] border-white/[0.1] text-white text-[10px]"
                  />
                </div>
                <div>
                  <Label className="text-[9px] text-white/40">
                    {t("brandTemplate.zones.fontSize")}
                  </Label>
                  <Input
                    type="number"
                    value={editingZone.fontSize}
                    onChange={(e) =>
                      updateZone(editingZone.id, { fontSize: parseInt(e.target.value) || 32 })
                    }
                    className="h-6 bg-[#030407] border-white/[0.1] text-white text-[10px]"
                    min={8}
                    max={200}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[9px] text-white/40">
                  {t("brandTemplate.zones.defaultText")}
                </Label>
                <Textarea
                  value={editingZone.defaultText}
                  onChange={(e) => updateZone(editingZone.id, { defaultText: e.target.value })}
                  placeholder={t("brandTemplate.zones.defaultTextPlaceholder")}
                  className="min-h-[40px] bg-[#030407] border-white/[0.1] text-white text-[10px] resize-none"
                  maxLength={500}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[9px] text-white/40">
                    {t("brandTemplate.zones.color")}
                  </Label>
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={editingZone.color}
                      onChange={(e) => updateZone(editingZone.id, { color: e.target.value })}
                      className="w-6 h-6 rounded border-0 cursor-pointer bg-transparent"
                    />
                    <Input
                      value={editingZone.color}
                      onChange={(e) => updateZone(editingZone.id, { color: e.target.value })}
                      className="h-6 bg-[#030407] border-white/[0.1] text-white text-[10px] flex-1"
                      maxLength={7}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[9px] text-white/40">
                    {t("brandTemplate.zones.fontWeight")}
                  </Label>
                  <Select
                    value={editingZone.fontWeight}
                    onValueChange={(v: "normal" | "bold") =>
                      updateZone(editingZone.id, { fontWeight: v })
                    }
                  >
                    <SelectTrigger className="h-6 bg-[#030407] border-white/[0.1] text-white text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                      {FONT_WEIGHT_OPTIONS.map((o) => (
                        <SelectItem key={o.id} value={o.id} className="text-white text-[10px]">
                          {t(o.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] text-white/40">
                    {t("brandTemplate.zones.align")}
                  </Label>
                  <Select
                    value={editingZone.textAlign}
                    onValueChange={(v: "left" | "center" | "right") =>
                      updateZone(editingZone.id, { textAlign: v })
                    }
                  >
                    <SelectTrigger className="h-6 bg-[#030407] border-white/[0.1] text-white text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                      {TEXT_ALIGN_OPTIONS.map((o) => (
                        <SelectItem key={o.id} value={o.id} className="text-white text-[10px]">
                          {t(o.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Zone list summary */}
          {zones.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {zones.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setEditingZoneId(z.id)}
                  className={`px-2 py-0.5 text-[9px] rounded border transition-colors ${
                    editingZoneId === z.id
                      ? "bg-purple-600/20 border-purple-500/40 text-purple-300"
                      : "bg-white/[0.03] border-white/[0.06] text-white/40 hover:text-white/60"
                  }`}
                >
                  <Type className="w-2.5 h-2.5 inline mr-0.5" />
                  {z.label}
                </button>
              ))}
            </div>
          )}

          {/* Save button */}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => saveZonesMutation.mutate()}
              disabled={saveZonesMutation.isPending || zones.length === 0}
              className="flex-1 h-7 text-[10px] bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saveZonesMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Save className="w-3 h-3 mr-1" />
              )}
              {t("brandTemplate.zones.save", { count: zones.length })}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (zones.length > 0 && activeTemplate) {
                  openForTextEditing(activeTemplate);
                } else {
                  setMode("list");
                }
              }}
              className="h-7 text-[10px] text-white/40 hover:text-white"
            >
              {zones.length > 0
                ? t("brandTemplate.zones.editText")
                : t("brandTemplate.zones.cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* ── EDIT TEXT MODE ── */}
      {mode === "edit-text" && activeTemplate && (
        <div className="space-y-2">
          {activeTemplate.textZones.length === 0 ? (
            <div className="text-center py-4 text-white/30 text-xs">
              <Type className="w-6 h-6 mx-auto mb-2 opacity-30" />
              <p>{t("brandTemplate.editText.noZones")}</p>
              <Button
                size="sm"
                onClick={() => openForZoneDefinition(activeTemplate)}
                className="mt-2 h-7 text-[10px] bg-purple-600 hover:bg-purple-700"
              >
                <Move className="w-3 h-3 mr-1" /> {t("brandTemplate.editText.defineZones")}
              </Button>
            </div>
          ) : (
            <>
              {/* Preview with text overlay */}
              <div className="relative rounded-lg overflow-hidden border border-white/[0.1]">
                <img
                  src={activeTemplate.imageUrl}
                  alt={activeTemplate.name}
                  className="w-full"
                  draggable={false}
                />
                {/* Text overlay preview */}
                {activeTemplate.textZones.map((zone) => {
                  const text = customTexts[zone.id] ?? zone.defaultText;
                  return (
                    <div
                      key={zone.id}
                      className="absolute flex items-center overflow-hidden pointer-events-none"
                      style={{
                        left: `${zone.x}%`,
                        top: `${zone.y}%`,
                        width: `${zone.width}%`,
                        height: `${zone.height}%`,
                        justifyContent:
                          zone.textAlign === "center"
                            ? "center"
                            : zone.textAlign === "right"
                              ? "flex-end"
                              : "flex-start",
                        padding: "2%",
                      }}
                    >
                      <span
                        style={{
                          color: zone.color,
                          fontSize: `clamp(8px, ${zone.fontSize * 0.04}vw, ${zone.fontSize * 0.5}px)`,
                          fontWeight: zone.fontWeight,
                          textAlign: zone.textAlign,
                          lineHeight: 1.2,
                          wordBreak: "break-word",
                        }}
                      >
                        {text}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Text input fields */}
              <div className="space-y-2">
                {activeTemplate.textZones.map((zone) => (
                  <div key={zone.id}>
                    <Label className="text-[9px] text-white/50 flex items-center gap-1 mb-0.5">
                      <Type className="w-2.5 h-2.5" />
                      {zone.label}
                      <span className="text-white/30">
                        ({zone.fontSize}px, {zone.fontWeight})
                      </span>
                    </Label>
                    <Textarea
                      value={customTexts[zone.id] ?? zone.defaultText}
                      onChange={(e) =>
                        setCustomTexts((prev) => ({ ...prev, [zone.id]: e.target.value }))
                      }
                      placeholder={zone.defaultText || t("brandTemplate.editText.placeholder")}
                      className="min-h-[36px] bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30 resize-none"
                      maxLength={500}
                    />
                  </div>
                ))}
              </div>

              {/* Rendered result */}
              {renderedUrl && (
                <div className="space-y-1.5">
                  <div className="relative rounded-lg overflow-hidden border border-emerald-500/30">
                    <img
                      src={renderedUrl}
                      alt={t("brandTemplate.editText.renderedAlt")}
                      className="w-full"
                    />
                  </div>
                  <a
                    href={renderedUrl}
                    download={`${activeTemplate.name.replace(/\s+/g, "-")}-rendered.png`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] transition-colors"
                  >
                    <Download className="w-3 h-3" /> {t("brandTemplate.editText.download")}
                  </a>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleRender}
                  disabled={rendering}
                  className="flex-1 h-7 text-[10px] bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  {rendering ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Eye className="w-3 h-3 mr-1" />
                  )}
                  {t("brandTemplate.editText.generate")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openForZoneDefinition(activeTemplate)}
                  className="h-7 text-[10px] text-white/40 hover:text-white"
                >
                  <Move className="w-3 h-3 mr-1" /> {t("brandTemplate.editText.editZones")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
